import { Info } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

const BUBBLE_HALF_WIDTH = 150;

/** An (i) button that toggles a short explanation bubble. Closes on outside click or Escape. */
export function InfoTip(props: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  const [align, setAlign] = useState<'center' | 'start' | 'end'>('center');
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const bubbleId = useId();

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  function toggle() {
    const rect = root.current?.getBoundingClientRect();
    if (rect) {
      setAlign(rect.left < BUBBLE_HALF_WIDTH ? 'start' : window.innerWidth - rect.right < BUBBLE_HALF_WIDTH ? 'end' : 'center');
    }
    setOpen((value) => !value);
  }

  return (
    <span className="info-tip" ref={root}>
      <button
        ref={button}
        type="button"
        className="info-tip-button"
        aria-label={`About ${props.label}`}
        aria-expanded={open}
        aria-describedby={open ? bubbleId : undefined}
        onClick={toggle}
      >
        <Info size={14} aria-hidden="true" />
      </button>
      {open ? (
        <span id={bubbleId} role="tooltip" className={`info-bubble info-bubble-${align}`}>
          {props.text}
        </span>
      ) : null}
    </span>
  );
}
